import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import KakaoShareButton from '../../components/feature/KakaoShareButton';
import type { ShareOrderItem } from '../../components/feature/KakaoShareButton';
import { useCart } from '../../context/CartContext';
import { deductStock } from '../../utils/stockManager';
import { mockReviews } from '../../mocks/reviews';
import { bestProducts } from '../../mocks/products';

// Payster(홀빅) 전역 함수 타입 선언
declare global {
  interface Window {
    SendPay: (form: HTMLFormElement) => void;
    pay_result_submit: () => void;
    pay_result_close: () => void;
  }
}

type PaymentMethod = 'card' | 'transfer' | 'kakaopay' | 'naverpay' | 'point' | 'shop_point';
type Step = 'info' | 'payment' | 'complete';

interface OrderForm {
  name: string;
  phone: string;
  email: string;
  zipcode: string;
  address: string;
  addressDetail: string;
  memo: string;
}

interface Coupon {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  label: string;
  minAmount?: number;
}

const VALID_COUPONS: Coupon[] = [
  { code: 'WELCOME10', type: 'percent', value: 10, label: '신규 회원 10% 할인', minAmount: 10000 },
  { code: 'SAVE5000', type: 'fixed', value: 5000, label: '5,000원 할인 쿠폰', minAmount: 30000 },
  { code: 'SUMMER20', type: 'percent', value: 20, label: '여름 특별 20% 할인', minAmount: 20000 },
  { code: 'FREE3000', type: 'fixed', value: 3000, label: '3,000원 즉시 할인' },
  { code: 'VIP15', type: 'percent', value: 15, label: 'VIP 회원 15% 할인', minAmount: 15000 },
];

// Payster pgAsistant.js 동적 로드
const loadPaysterScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (typeof window.SendPay === 'function') { resolve(); return; }
    const existing = document.getElementById('payster-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('결제 모듈 로드 실패')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'payster-script';
    script.src = 'https://api.payster.co.kr/js/pgAsistant.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('결제 모듈 로드 실패'));
    document.head.appendChild(script);
  });

const WALLET_ADDRESS = '0x10881B8AF28627474857e8dFedBEB6194433af42';
const SMS_RECIPIENT = '01053308579';

export default function OrderPage() {
  const { items, getTotalPrice, clearCart, addItem } = useCart();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>('info');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [quickBuyReady, setQuickBuyReady] = useState(false);
  const [orderNumber] = useState(() => 'ORD-' + Date.now().toString().slice(-8));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<OrderForm>>({});
  const [payError, setPayError] = useState<string | null>(null);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [orderItems, setOrderItems] = useState<ShareOrderItem[]>([]);

  // TX Hash 관련 상태
  const [txHash, setTxHash] = useState('');
  const [txHashError, setTxHashError] = useState<string | null>(null);

  // 쇼핑 포인트 결제 상태 (회원ID 단독 방식)
  const [pointPhone, setPointPhone] = useState('');
  const [pointBalance, setPointBalance] = useState<number | null>(null);
  const [isCheckingPoints, setIsCheckingPoints] = useState(false);
  const [pointCheckError, setPointCheckError] = useState<string | null>(null);
  const [pointChecked, setPointChecked] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [memberName, setMemberName] = useState<string | null>(null);

  // 쿠폰 관련 상태
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null);
  const [isCouponApplying, setIsCouponApplying] = useState(false);

  const [form, setForm] = useState<OrderForm>({
    name: '', phone: '', email: '',
    zipcode: '', address: '', addressDetail: '', memo: '',
  });

  // 빠른 구매 진입 처리: ?product_id=...&from_points=1&user_id=...
  useEffect(() => {
    const productId = searchParams.get('product_id');
    const fromPoints = searchParams.get('from_points');
    const userId = searchParams.get('user_id');

    if (productId && fromPoints === '1') {
      const product = bestProducts.find((p) => String(p.id) === productId);
      if (product && items.length === 0) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: product.originalPrice,
          image: product.image,
          category: product.category,
          quantity: 1,
        });
      }
      // 쇼핑 포인트 결제 자동 선택
      setPaymentMethod('shop_point');
      // 회원ID 자동 입력
      if (userId) {
        setMemberId(decodeURIComponent(userId).toUpperCase());
      }
      setQuickBuyReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatPrice = (price: number) => price.toLocaleString('ko-KR');

  const getPaymentLabel = (method: PaymentMethod) => {
    switch (method) {
      case 'card': return '카드결제';
      case 'transfer': return '계좌이체';
      case 'kakaopay': return '카카오페이';
      case 'naverpay': return '네이버페이';
      case 'point': return 'USDT 포인트결제';
      case 'shop_point': return '쇼핑 포인트 결제';
    }
  };

  const getDiscountAmount = (): number => {
    if (!appliedCoupon) return 0;
    const total = getTotalPrice();
    if (appliedCoupon.type === 'percent') return Math.floor(total * (appliedCoupon.value / 100));
    return Math.min(appliedCoupon.value, total);
  };

  const getFinalPrice = (): number => Math.max(0, getTotalPrice() - getDiscountAmount());

  const handleApplyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError('쿠폰 코드를 입력해주세요.'); return; }
    setIsCouponApplying(true);
    setCouponError(null);
    setCouponSuccess(null);
    setTimeout(() => {
      const found = VALID_COUPONS.find((c) => c.code === code);
      if (!found) { setCouponError('유효하지 않은 쿠폰 코드입니다.'); setIsCouponApplying(false); return; }
      if (found.minAmount && getTotalPrice() < found.minAmount) {
        setCouponError(`최소 주문금액 ${formatPrice(found.minAmount)}원 이상 시 사용 가능합니다.`);
        setIsCouponApplying(false); return;
      }
      setAppliedCoupon(found);
      const discountText = found.type === 'percent' ? `${found.value}% 할인` : `${formatPrice(found.value)}원 할인`;
      setCouponSuccess(`쿠폰이 적용되었습니다! (${discountText})`);
      setIsCouponApplying(false);
    }, 600);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null); setCouponInput('');
    setCouponError(null); setCouponSuccess(null);
  };

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(WALLET_ADDRESS).then(() => {
      setCopiedWallet(true);
      setTimeout(() => setCopiedWallet(false), 2000);
    });
  };

  const saveOrderToHistory = (method: string) => {
    const newOrder = {
      orderNumber, date: new Date().toISOString(),
      status: method === '계좌이체' ? 'pending' : 'confirmed',
      paymentMethod: method,
      items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, category: i.category })),
      totalPrice: getFinalPrice(), originalPrice: getTotalPrice(),
      discountAmount: getDiscountAmount(), couponCode: appliedCoupon?.code || null,
      shippingAddress: `${form.address} ${form.addressDetail}`.trim(),
      name: form.name, phone: form.phone, email: form.email, memo: form.memo,
    };
    const existing = localStorage.getItem('orders');
    const orders = existing ? JSON.parse(existing) : [];
    orders.unshift(newOrder);
    localStorage.setItem('orders', JSON.stringify(orders));
    deductStock(items.map((i) => ({ id: i.id, quantity: i.quantity })));
  };

  const validate = (): boolean => {
    const newErrors: Partial<OrderForm> = {};
    if (!form.name.trim()) newErrors.name = '이름을 입력해주세요.';
    if (!form.phone.trim()) newErrors.phone = '연락처를 입력해주세요.';
    if (!form.email.trim()) newErrors.email = '이메일을 입력해주세요.';
    if (!form.address.trim()) newErrors.address = '주소를 입력해주세요.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof OrderForm]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleNextStep = () => { if (validate()) setStep('payment'); };

  const sendOrderSMS = async ({
    paymentMethod,
    totalPrice,
    itemsSummary,
    txHash,
  }: {
    paymentMethod: string;
    totalPrice: string;
    itemsSummary: string;
    txHash?: string;
  }) => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
    try {
      await fetch(`${supabaseUrl}/functions/v1/sms-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          to: SMS_RECIPIENT,
          orderNumber,
          name: form.name,
          phone: form.phone,
          paymentMethod,
          totalPrice,
          items: itemsSummary,
          ...(txHash ? { txHash } : {}),
        }),
      });
    } catch {
      // ignore
    }
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const itemsSummary = items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | ');
    const formData = new URLSearchParams();
    formData.append('orderNumber', orderNumber);
    formData.append('name', form.name);
    formData.append('phone', form.phone);
    formData.append('email', form.email);
    formData.append('address', `${form.address} ${form.addressDetail}`.trim());
    formData.append('memo', form.memo);
    formData.append('paymentMethod', '계좌이체');
    formData.append('items', itemsSummary);
    formData.append('totalPrice', `${formatPrice(getFinalPrice())}원`);
    if (appliedCoupon) {
      formData.append('couponCode', appliedCoupon.code);
      formData.append('discountAmount', `${formatPrice(getDiscountAmount())}원`);
    }
    try {
      await fetch('https://readdy.ai/api/form/d6ks1ho5dsf7sr1n5lag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (err) {
      void err;
    }
    setOrderItems(items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })));
    saveOrderToHistory('계좌이체');
    clearCart();
    await sendOrderSMS({
      paymentMethod: '계좌이체',
      totalPrice: `${formatPrice(getFinalPrice())}원`,
      itemsSummary,
    });
    setIsSubmitting(false);
    setStep('complete');
  };

  // Payster(홀빅) PG 카드결제
  const handleCardSubmit = async () => {
    setIsSubmitting(true);
    setPayError(null);

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
    const finalPrice = getFinalPrice();
    const productName = items.length === 1 ? items[0].name : `${items[0].name} 외 ${items.length - 1}건`;

    try {
      await loadPaysterScript();

      const hashRes = await fetch(`${supabaseUrl}/functions/v1/payletter-payment/hash`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ goodsAmt: finalPrice }),
      });

      if (!hashRes.ok) throw new Error('해시 생성에 실패했습니다.');

      const { ediDate, encData, merchantId } = await hashRes.json();
      if (!ediDate || !encData) throw new Error('결제 인증 정보를 가져올 수 없습니다.');

      const payForm = document.createElement('form');
      payForm.name = 'payInit';
      payForm.method = 'post';
      payForm.action = `${window.location.origin}/order`;
      payForm.style.display = 'none';

      const notiUrl = `${supabaseUrl}/functions/v1/payletter-payment/callback`;
      const fields: Record<string, string> = {
        payMethod: 'card', mid: merchantId,
        goodsNm: productName.length > 50 ? productName.slice(0, 47) + '...' : productName,
        ordNo: orderNumber, goodsAmt: String(finalPrice),
        ordNm: form.name, ordTel: form.phone, ordEmail: form.email,
        returnUrl: `${window.location.origin}/order`, notiUrl,
        userIp: '127.0.0.1', trxCd: '0',
        mbsUsrId: form.email || 'guest', charSet: 'UTF-8', ediDate, encData,
      };

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        payForm.appendChild(input);
      });

      document.body.appendChild(payForm);

      window.pay_result_submit = () => {
        document.body.removeChild(payForm);
        setOrderItems(items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })));
        saveOrderToHistory('카드결제');
        clearCart();
        void sendOrderSMS({
          paymentMethod: '카드결제',
          totalPrice: `${formatPrice(finalPrice)}원`,
          itemsSummary: items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | '),
        });
        setIsSubmitting(false);
        setPayError(null);
        setStep('complete');
      };

      window.pay_result_close = () => {
        document.body.removeChild(payForm);
        setPayError('결제가 취소되었습니다. 다시 시도하시려면 결제 버튼을 눌러주세요.');
        setIsSubmitting(false);
      };

      window.SendPay(payForm);
    } catch (err) {
      console.error('결제 오류:', err);
      const msg = err instanceof Error ? err.message : '알 수 없는 오류';
      if (msg.includes('로드 실패')) {
        setPayError('결제 모듈을 불러올 수 없습니다. 인터넷 연결을 확인해주세요.');
      } else if (msg.includes('해시') || msg.includes('인증')) {
        setPayError('결제 인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setPayError('결제 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
      setIsSubmitting(false);
    }
  };

  const handlePointSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // TX Hash 유효성 검사
    if (!txHash.trim()) {
      setTxHashError('트랜잭션 해시(TX Hash)를 입력해주세요.');
      return;
    }
    if (txHash.trim().length < 60) {
      setTxHashError('유효하지 않은 트랜잭션 해시입니다. 올바른 TX Hash를 입력해주세요.');
      return;
    }
    setTxHashError(null);
    setIsSubmitting(true);

    const itemsSummary = items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | ');
    const formData = new URLSearchParams();
    formData.append('orderNumber', orderNumber);
    formData.append('name', form.name);
    formData.append('phone', form.phone);
    formData.append('email', form.email);
    formData.append('address', `${form.address} ${form.addressDetail}`.trim());
    formData.append('memo', form.memo);
    formData.append('paymentMethod', '포인트결제(USDT)');
    formData.append('walletAddress', WALLET_ADDRESS);
    formData.append('txHash', txHash.trim());
    formData.append('items', itemsSummary);
    formData.append('totalPrice', `${formatPrice(getFinalPrice())}원`);
    if (appliedCoupon) {
      formData.append('couponCode', appliedCoupon.code);
      formData.append('discountAmount', `${formatPrice(getDiscountAmount())}원`);
    }

    try {
      await fetch('https://readdy.ai/api/form/d6ks1ho5dsf7sr1n5lag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (err) {
      void err;
    }

    // SMS 알림 전송
    await sendOrderSMS({
      paymentMethod: '포인트결제(USDT)',
      totalPrice: `${formatPrice(getFinalPrice())}원`,
      itemsSummary: items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | '),
      txHash: txHash.trim(),
    });

    setOrderItems(items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })));
    saveOrderToHistory('포인트결제');
    clearCart();
    setIsSubmitting(false);
    setStep('complete');
  };

  const handleCheckPoints = async () => {
    const inputVal = memberId.trim();
    if (!inputVal) {
      setPointCheckError('회원ID를 입력해주세요. (예: MEM001)');
      return;
    }
    setIsCheckingPoints(true);
    setPointCheckError(null);
    setPointChecked(false);
    setMemberName(null);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/shop-points`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ action: 'check', member_id: inputVal }),
      });
      const data = await res.json();
      if (data.success) {
        setPointBalance(data.points_balance);
        setPointChecked(true);
        if (data.member_name) setMemberName(data.member_name);
        if (data.phone) setPointPhone(data.phone);
      } else {
        setPointCheckError(data.msg || '잔액 조회에 실패했습니다.');
        setPointBalance(null);
      }
    } catch {
      setPointCheckError('서버 연결에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsCheckingPoints(false);
    }
  };

  const handleShopPointSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pointChecked || pointBalance === null) {
      setPointCheckError('먼저 포인트 잔액을 조회해주세요.');
      return;
    }
    const finalPrice = getFinalPrice();
    if (pointBalance < finalPrice) {
      setPointCheckError(`포인트 잔액이 부족합니다. (현재: ${formatPrice(pointBalance)}P, 필요: ${formatPrice(finalPrice)}P)`);
      return;
    }
    setIsSubmitting(true);
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
    const itemsSummary = items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | ');

    const spendPayload = {
      action: 'spend',
      member_id: memberId.trim(),
      amount: finalPrice,
      order_number: orderNumber,
      description: `주문 결제 - ${items.length > 1 ? `${items[0].name} 외 ${items.length - 1}건` : items[0]?.name}`,
    };

    const spendRes = await fetch(`${supabaseUrl}/functions/v1/shop-points`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify(spendPayload),
    });
    const spendData = await spendRes.json();

    if (!spendData.success) {
      const realBalance = spendData.points_balance;
      if (realBalance !== undefined && realBalance !== null) {
        setPointBalance(realBalance);
      } else {
        setPointBalance(null);
        setPointChecked(false);
      }
      setPointCheckError(spendData.msg || '포인트 결제에 실패했습니다.');
      setIsSubmitting(false);
      return;
    }

    setPointBalance(spendData.remaining_balance ?? null);
    setPointChecked(false);

    const formData = new URLSearchParams();
    formData.append('orderNumber', orderNumber);
    formData.append('name', form.name);
    formData.append('phone', form.phone);
    formData.append('email', form.email);
    formData.append('address', `${form.address} ${form.addressDetail}`.trim());
    formData.append('memo', form.memo);
    formData.append('paymentMethod', '쇼핑 포인트 결제');
    formData.append('memberId', memberId.trim());
    if (memberName) formData.append('memberName', memberName);
    formData.append('usedPoints', `${formatPrice(finalPrice)}P`);
    formData.append('remainingPoints', `${formatPrice(spendData.remaining_balance)}P`);
    formData.append('items', itemsSummary);
    formData.append('totalPrice', `${formatPrice(finalPrice)}원`);
    if (appliedCoupon) {
      formData.append('couponCode', appliedCoupon.code);
      formData.append('discountAmount', `${formatPrice(getDiscountAmount())}원`);
    }

    try {
      await fetch('https://readdy.ai/api/form/d6ks1ho5dsf7sr1n5lag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (err) {
      void err;
    }

    await sendOrderSMS({
      paymentMethod: '쇼핑 포인트 결제',
      totalPrice: `${formatPrice(finalPrice)}원 (${formatPrice(finalPrice)}P 사용)`,
      itemsSummary,
    });

    setOrderItems(items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })));
    saveOrderToHistory('쇼핑 포인트 결제');
    clearCart();
    setIsSubmitting(false);
    setStep('complete');
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentMethod === 'card') {
      await handleCardSubmit();
    } else if (paymentMethod === 'kakaopay' || paymentMethod === 'naverpay') {
      setIsSubmitting(true);
      await new Promise((r) => setTimeout(r, 800));
      const label = getPaymentLabel(paymentMethod);
      const itemsSummary = items.map((i) => `${i.name} x${i.quantity} (${formatPrice(i.price * i.quantity)}원)`).join(' | ');
      setOrderItems(items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })));
      saveOrderToHistory(label);
      clearCart();
      await sendOrderSMS({
        paymentMethod: label,
        totalPrice: `${formatPrice(getFinalPrice())}원`,
        itemsSummary,
      });
      setIsSubmitting(false);
      setStep('complete');
    } else if (paymentMethod === 'point') {
      await handlePointSubmit(e);
    } else if (paymentMethod === 'shop_point') {
      await handleShopPointSubmit(e);
    } else {
      await handleTransferSubmit(e);
    }
  };

  const handleRetryPayment = () => {
    setPayError(null);
    setIsSubmitting(false);
  };

  if (items.length === 0 && step !== 'complete') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
              <i className="ri-shopping-cart-line text-3xl text-gray-400 w-8 h-8 flex items-center justify-center"></i>
            </div>
            <p className="text-gray-600 mb-6">장바구니가 비어있습니다.</p>
            <Link to="/products" className="px-6 py-3 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
              제품 둘러보기
            </Link>
          </div>
        </div>
        <PageFooter />
      </>
    );
  }

  if (step === 'complete') {
    const shareTitle = `✅ 주문 완료 — ${orderNumber}`;
    const shareDesc = [
      `결제방법: ${getPaymentLabel(paymentMethod)}`,
      `결제금액: ${formatPrice(getFinalPrice())}원`,
      getDiscountAmount() > 0 ? `할인적용: -${formatPrice(getDiscountAmount())}원` : '',
      paymentMethod === 'point' ? `USDT 지갑: ${WALLET_ADDRESS}` : '',
      paymentMethod === 'point' && txHash ? `TX Hash: ${txHash}` : '',
      paymentMethod === 'shop_point' ? `포인트 사용: ${formatPrice(getFinalPrice())}P` : '',
    ].filter(Boolean).join('\n');

    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 pb-16 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 md:p-12 max-w-md w-full text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-emerald-50 rounded-full">
              <i className="ri-checkbox-circle-fill text-4xl text-emerald-500 w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              주문이 완료되었습니다!
            </h2>
            <p className="text-gray-500 text-sm mb-6">감사합니다. 주문이 정상적으로 접수되었습니다.</p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">주문번호</span>
                <span className="font-semibold text-gray-900">{orderNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">결제방법</span>
                <span className="font-semibold text-gray-900">{getPaymentLabel(paymentMethod)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">결제금액</span>
                <span className="font-bold text-emerald-600">{formatPrice(getFinalPrice())}원</span>
              </div>
              {getDiscountAmount() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">할인 금액</span>
                  <span className="font-semibold text-red-500">-{formatPrice(getDiscountAmount())}원</span>
                </div>
              )}
              {paymentMethod === 'transfer' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">입금 계좌 안내</p>
                  <p className="text-sm font-bold text-emerald-700">IBK기업은행 010-5330-8579</p>
                  <p className="text-xs text-gray-500">예금주: 신경진</p>
                  <p className="text-xs text-emerald-600 mt-1">주문번호를 입금자명에 기재해주세요.</p>
                </div>
              )}
              {paymentMethod === 'point' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-700">USDT</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">ERC-20</span>
                    <span className="text-xs text-gray-500 ml-1">지갑 주소 재확인</span>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-3 flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-white rounded-lg border border-teal-200 flex items-center justify-center overflow-hidden">
                        <img
                          src="https://api.qrserver.com/v1/create-qr-code/?size=64x64&data=0x10881B8AF28627474857e8dFedBEB6194433af42&format=png&margin=1"
                          alt="USDT 지갑 QR코드"
                          className="w-full h-full object-contain"
                          width={64}
                          height={64}
                        />
                      </div>
                      <p className="text-[9px] text-teal-500 text-center mt-1 font-semibold">QR 스캔</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-teal-600 font-semibold mb-1">전송 지갑 주소</p>
                      <p className="text-[11px] font-mono font-bold text-teal-700 break-all select-all leading-relaxed">
                        {WALLET_ADDRESS}
                      </p>
                    </div>
                  </div>
                  {txHash && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">제출된 TX Hash</p>
                      <p className="text-xs font-mono text-gray-700 break-all">{txHash}</p>
                      <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                        <i className="ri-message-2-line w-3 h-3 flex items-center justify-center"></i>
                        TX Hash가 관리자에게 문자로 전송되었습니다.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {paymentMethod === 'shop_point' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg">
                        <i className="ri-coin-line text-emerald-600 w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-xs font-bold text-emerald-800">쇼핑 포인트 결제 완료</p>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between text-emerald-700">
                        <span>사용 포인트</span>
                        <span className="font-bold">-{formatPrice(getFinalPrice())}P</span>
                      </div>
                      <p className="text-emerald-600 flex items-center gap-1 mt-1">
                        <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center"></i>
                        포인트가 정상적으로 차감되었습니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 카카오톡 공유 */}
            <div className="mb-4">
              <KakaoShareButton
                title={shareTitle}
                description={shareDesc}
                orderNumber={orderNumber}
                items={orderItems}
                totalAmount={`${formatPrice(getFinalPrice())}원`}
                paymentMethod={getPaymentLabel(paymentMethod)}
              />
            </div>

            <div className="space-y-3">
              <Link to="/order-history" className="block w-full py-3 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
                주문 내역 확인하기
              </Link>
              <Link to="/" className="block w-full py-3 border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-emerald-600 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">
                홈으로 돌아가기
              </Link>
              <Link to="/products" className="block w-full py-3 border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-emerald-600 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">
                계속 쇼핑하기
              </Link>
            </div>
          </div>
        </div>
        <PageFooter />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* 빠른 구매 진입 안내 배너 */}
          {quickBuyReady && (
            <div className="mt-4 mb-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-4 md:p-5 text-white flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-flashlight-line text-white text-xl w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black mb-0.5">포인트 빠른 구매 모드</p>
                <p className="text-xs text-emerald-100">
                  제품이 자동으로 담겼고, <strong className="text-white">쇼핑 포인트 결제</strong>가 선택되었습니다.
                  {memberId && <> 회원ID <code className="font-mono bg-white/20 px-1 rounded">{memberId}</code>가 자동 입력되었습니다.</>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setQuickBuyReady(false)}
                className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg cursor-pointer flex-shrink-0 transition-colors"
              >
                <i className="ri-close-line text-white w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          )}

          {/* 단계 표시 */}
          <div className="flex items-center justify-center gap-3 mb-8 md:mb-10 pt-4">
            {(['info', 'payment'] as Step[]).map((s, idx) => (
              <div key={s} className="flex items-center gap-3">
                <div className={`flex items-center gap-2 ${step === s ? 'text-emerald-600' : s === 'info' && step === 'payment' ? 'text-emerald-400' : 'text-gray-400'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === s ? 'bg-emerald-600 text-white' : s === 'info' && step === 'payment' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-500'}`}>
                    {s === 'info' && step === 'payment' ? <i className="ri-check-line w-3 h-3 flex items-center justify-center"></i> : idx + 1}
                  </div>
                  <span className="text-sm font-semibold hidden sm:block">{s === 'info' ? '배송 정보' : '결제 방법'}</span>
                </div>
                {idx < 1 && <div className="w-8 h-px bg-gray-300"></div>}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {/* 왼쪽: 폼 영역 */}
            <div className="lg:col-span-2">
              {step === 'info' && (
                <div className="bg-white rounded-2xl p-5 md:p-8">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                    배송 정보 입력
                  </h2>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">이름 <span className="text-red-500">*</span></label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="홍길동"
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 ${errors.name ? 'border-red-400' : 'border-gray-200'}`} />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">연락처 <span className="text-red-500">*</span></label>
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000"
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 ${errors.phone ? 'border-red-400' : 'border-gray-200'}`} />
                        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">이메일 <span className="text-red-500">*</span></label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="example@email.com"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 ${errors.email ? 'border-red-400' : 'border-gray-200'}`} />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">주소 <span className="text-red-500">*</span></label>
                      <input type="text" name="address" value={form.address} onChange={handleChange} placeholder="도로명 주소를 입력해주세요"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 mb-2 ${errors.address ? 'border-red-400' : 'border-gray-200'}`} />
                      {errors.address && <p className="text-xs text-red-500 mb-2">{errors.address}</p>}
                      <input type="text" name="addressDetail" value={form.addressDetail} onChange={handleChange} placeholder="상세 주소 (동, 호수 등)"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">배송 메모</label>
                      <textarea name="memo" value={form.memo} onChange={handleChange} placeholder="배송 시 요청사항을 입력해주세요 (선택)" rows={3} maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none" />
                      <p className="text-xs text-gray-400 text-right mt-1">{form.memo.length}/500</p>
                    </div>
                  </div>
                  <button type="button" onClick={handleNextStep}
                    className="w-full mt-6 py-3.5 bg-emerald-600 text-white rounded-full font-bold text-base hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
                    다음 단계 — 결제 방법 선택
                  </button>
                </div>
              )}

              {step === 'payment' && (
                <form data-readdy-form onSubmit={handlePaymentSubmit} className="bg-white rounded-2xl p-5 md:p-8">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                    결제 방법 선택
                  </h2>

                  {/* 결제 방법 선택 */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    {/* 카드결제 */}
                    <button type="button" onClick={() => setPaymentMethod('card')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer ${paymentMethod === 'card' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'card' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-3">
                        <i className="ri-bank-card-line text-xl text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <p className="font-bold text-gray-900 text-sm mb-1">카드결제</p>
                      <p className="text-xs text-gray-500">신용카드 / 체크카드</p>
                    </button>

                    {/* 계좌이체 */}
                    <button type="button" onClick={() => setPaymentMethod('transfer')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer ${paymentMethod === 'transfer' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'transfer' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg mb-3">
                        <i className="ri-exchange-dollar-line text-xl text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <p className="font-bold text-gray-900 text-sm mb-1">계좌이체</p>
                      <p className="text-xs text-gray-500">무통장 입금</p>
                    </button>

                    {/* 카카오페이 */}
                    <button type="button" onClick={() => setPaymentMethod('kakaopay')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer ${paymentMethod === 'kakaopay' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'kakaopay' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-yellow-400 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-lg mb-3">
                        <span className="text-sm font-black text-gray-900 leading-none">K</span>
                      </div>
                      <p className="font-bold text-gray-900 text-sm mb-1">카카오페이</p>
                      <p className="text-xs text-gray-500">카카오 간편결제</p>
                    </button>

                    {/* 네이버페이 */}
                    <button type="button" onClick={() => setPaymentMethod('naverpay')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer ${paymentMethod === 'naverpay' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'naverpay' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-green-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-lg mb-3">
                        <span className="text-sm font-black text-white leading-none">N</span>
                      </div>
                      <p className="font-bold text-gray-900 text-sm mb-1">네이버페이</p>
                      <p className="text-xs text-gray-500">네이버 간편결제</p>
                    </button>

                    {/* USDT 포인트 결제 */}
                    <button type="button" onClick={() => setPaymentMethod('point')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer sm:col-span-2 ${paymentMethod === 'point' ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'point' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-teal-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                          <span className="text-xs font-black text-teal-600 leading-none">USDT</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-bold text-gray-900 text-sm">포인트 결제</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-700">USDT</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">ERC-20</span>
                          </div>
                          <p className="text-xs text-gray-500">USDT 지갑 주소로 포인트를 전송하여 결제</p>
                        </div>
                      </div>
                    </button>

                    {/* 쇼핑 포인트 결제 */}
                    <button type="button" onClick={() => setPaymentMethod('shop_point')}
                      className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer sm:col-span-2 ${paymentMethod === 'shop_point' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {paymentMethod === 'shop_point' && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                          <i className="ri-coin-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-bold text-gray-900 text-sm">쇼핑 포인트 결제</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">전산 포인트</span>
                          </div>
                          <p className="text-xs text-gray-500">ERP 전산에서 적립된 포인트로 결제</p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* 카드결제 안내 */}
                  {paymentMethod === 'card' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                          <i className="ri-bank-card-line text-emerald-600 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-emerald-800 mb-1">홀빅(Payster) 안전결제</p>
                          <p className="text-xs text-emerald-700">
                            결제 버튼 클릭 시 <strong>홀빅 PG 결제창</strong>이 팝업으로 열립니다.
                          </p>
                          <p className="text-xs text-emerald-600 mt-1.5">
                            카드 정보는 안전한 결제창에서 직접 입력하시며, SSL 암호화로 보호됩니다.
                          </p>
                          <div className="mt-2 pt-2 border-t border-emerald-200">
                            <p className="text-xs text-emerald-500">
                              팝업 차단 시 브라우저 설정에서 팝업을 허용해주세요.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 계좌이체 안내 */}
                  {paymentMethod === 'transfer' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <p className="text-sm font-bold text-amber-800 mb-1 flex items-center gap-1.5">
                        <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
                        입금 계좌 안내
                      </p>
                      <div className="space-y-1 text-sm text-amber-700">
                        <p><span className="font-semibold">은행:</span> IBK기업은행</p>
                        <p><span className="font-semibold">계좌번호:</span> 010-5330-8579</p>
                        <p><span className="font-semibold">예금주:</span> 신경진</p>
                      </div>
                      <p className="text-xs text-amber-600 mt-2 pt-2 border-t border-amber-200">
                        주문번호({orderNumber})를 입금자명에 기재해주세요.
                      </p>
                    </div>
                  )}

                  {paymentMethod === 'kakaopay' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                      <p className="text-sm font-bold text-yellow-800 mb-1">카카오페이 간편결제</p>
                      <p className="text-xs text-yellow-700">카카오페이 앱 또는 카카오톡을 통해 결제하실 수 있습니다.</p>
                    </div>
                  )}

                  {paymentMethod === 'naverpay' && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                      <p className="text-sm font-bold text-green-800 mb-1">네이버페이 간편결제</p>
                      <p className="text-xs text-green-700">네이버 아이디로 로그인 후 간편하게 결제하실 수 있습니다.</p>
                    </div>
                  )}

                  {/* USDT 포인트 결제 안내 + TX Hash 입력 */}
                  {paymentMethod === 'point' && (
                    <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black bg-teal-500 text-white">USDT</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">ERC-20 네트워크</span>
                        <p className="text-sm font-bold text-teal-800">포인트 결제 안내</p>
                      </div>

                      <div className="space-y-3">
                        {/* 지갑 주소 */}
                        <div>
                          <p className="text-xs text-teal-600 font-semibold mb-1.5">USDT 전송 지갑 주소</p>
                          <div className="flex items-center gap-2 bg-white border border-teal-200 rounded-lg px-3 py-2.5">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                              <i className="ri-wallet-3-line text-teal-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                            </div>
                            <span className="flex-1 text-xs font-mono text-gray-800 break-all select-all">{WALLET_ADDRESS}</span>
                            <button
                              type="button"
                              onClick={handleCopyWallet}
                              className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                copiedWallet ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                              }`}
                            >
                              {copiedWallet ? (
                                <><i className="ri-check-line w-3 h-3 flex items-center justify-center"></i>복사됨</>
                              ) : (
                                <><i className="ri-file-copy-line w-3 h-3 flex items-center justify-center"></i>복사</>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* 안내사항 */}
                        <div className="bg-white border border-teal-100 rounded-lg p-3 space-y-1.5">
                          <p className="text-xs font-semibold text-teal-800 mb-1">전송 전 반드시 확인하세요</p>
                          <p className="text-xs text-teal-700 flex items-start gap-1.5">
                            <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-teal-500"></i>
                            네트워크: <strong>ERC-20 (이더리움)</strong> 네트워크로만 전송해주세요.
                          </p>
                          <p className="text-xs text-teal-700 flex items-start gap-1.5">
                            <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-teal-500"></i>
                            코인: <strong>USDT (Tether)</strong> 만 지원합니다.
                          </p>
                          <p className="text-xs text-teal-700 flex items-start gap-1.5">
                            <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-teal-500"></i>
                            주문금액에 해당하는 USDT 전송 후 아래 TX Hash를 입력해주세요.
                          </p>
                          <p className="text-xs text-orange-600 flex items-start gap-1.5 pt-1 border-t border-teal-100">
                            <i className="ri-alert-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                            다른 네트워크로 전송 시 자산이 손실될 수 있습니다.
                          </p>
                        </div>

                        {/* TX Hash 입력 박스 */}
                        <div className="bg-white border-2 border-teal-200 rounded-xl p-4">
                          <p className="text-xs font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                            <i className="ri-fingerprint-line w-3.5 h-3.5 flex items-center justify-center text-teal-600"></i>
                            트랜잭션 해시 (TX Hash) 입력
                            <span className="text-red-500 ml-0.5">*</span>
                          </p>
                          <p className="text-xs text-gray-500 mb-2.5 leading-relaxed">
                            USDT 전송 완료 후 거래소 또는 지갑에서 확인한 TX Hash를 입력해주세요.
                            입력하신 정보는 <strong className="text-teal-600">010-5330-8579</strong>로 문자 전송됩니다.
                          </p>
                          <textarea
                            value={txHash}
                            onChange={(e) => { setTxHash(e.target.value); setTxHashError(null); }}
                            placeholder="0x로 시작하는 트랜잭션 해시를 입력하세요 (예: 0x3a5b7c...)"
                            rows={3}
                            maxLength={200}
                            className={`w-full px-3 py-2.5 border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none transition-colors ${
                              txHashError ? 'border-red-400 bg-red-50' : txHash && txHash.trim().length >= 60 ? 'border-teal-400 bg-teal-50/30' : 'border-gray-200'
                            }`}
                          />
                          {txHashError && (
                            <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                              <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center"></i>
                              {txHashError}
                            </p>
                          )}
                          {txHash && !txHashError && txHash.trim().length >= 60 && (
                            <p className="text-xs text-teal-600 mt-1.5 flex items-center gap-1">
                              <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                              유효한 TX Hash가 입력되었습니다.
                            </p>
                          )}
                          <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-start gap-1.5">
                            <i className="ri-message-2-line w-3.5 h-3.5 flex items-center justify-center text-gray-400 flex-shrink-0 mt-0.5"></i>
                            <p className="text-xs text-gray-400">
                              주문 완료 시 TX Hash 및 주문 정보가 관리자(010-5330-8579)에게 문자로 자동 전송됩니다.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 쇼핑 포인트 결제 섹션 */}
                  {paymentMethod === 'shop_point' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
                          <i className="ri-coin-line text-white w-4 h-4 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-emerald-800">쇼핑 포인트 결제</p>
                          <p className="text-xs text-emerald-600">ERP 전산에서 적립된 포인트를 사용합니다</p>
                        </div>
                      </div>

                      {/* 결제할 금액 */}
                      <div className="bg-white border border-emerald-200 rounded-xl p-3 mb-4 flex items-center justify-between">
                        <span className="text-sm text-gray-600 flex items-center gap-1.5">
                          <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center text-emerald-600"></i>
                          필요 포인트
                        </span>
                        <span className="text-xl font-black text-emerald-700">{formatPrice(getFinalPrice())}P</span>
                      </div>

                      {/* 입력 영역 */}
                      <div className="space-y-2 mb-4">
                        <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                          <i className="ri-id-card-line w-3.5 h-3.5 flex items-center justify-center text-emerald-600"></i>
                          회원ID 입력 (예: MEM001)
                        </p>
                        <div className="flex gap-2">
                          <input
                              type="text"
                              value={memberId}
                              onChange={(e) => {
                                setMemberId(e.target.value.toUpperCase());
                                setPointCheckError(null);
                                setPointChecked(false);
                                setPointBalance(null);
                                setMemberName(null);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && handleCheckPoints()}
                              placeholder="MEM001"
                              className={`flex-1 px-3 py-2.5 border-2 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white transition-colors uppercase ${
                                pointCheckError ? 'border-red-300' : 'border-emerald-200'
                              }`}
                            />
                          <button
                            type="button"
                            onClick={handleCheckPoints}
                            disabled={isCheckingPoints}
                            className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center gap-1.5"
                          >
                            {isCheckingPoints ? (
                              <><i className="ri-loader-4-line animate-spin w-3 h-3 flex items-center justify-center"></i>조회</>
                            ) : (
                              <><i className="ri-search-line w-3 h-3 flex items-center justify-center"></i>잔액확인</>
                            )}
                          </button>
                        </div>

                        {pointCheckError && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {pointCheckError}
                          </p>
                        )}

                        {pointChecked && pointBalance !== null && !pointCheckError && (
                          <div className={`p-3 rounded-lg border ${
                            pointBalance >= getFinalPrice()
                              ? 'bg-emerald-50 border-emerald-300'
                              : 'bg-red-50 border-red-300'
                          }`}>
                            {memberName && (
                              <p className="text-xs font-bold text-emerald-800 mb-1.5 flex items-center gap-1">
                                <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                                {memberName} 회원님
                                {memberId && <span className="ml-1 text-emerald-500 font-mono">({memberId})</span>}
                              </p>
                            )}
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">현재 잔액</span>
                              <span className={`text-sm font-black ${pointBalance >= getFinalPrice() ? 'text-emerald-700' : 'text-red-600'}`}>
                                {formatPrice(pointBalance)}P
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">결제 후 잔액</span>
                              <span className={`text-xs font-bold ${pointBalance >= getFinalPrice() ? 'text-emerald-600' : 'text-red-500'}`}>
                                {pointBalance >= getFinalPrice()
                                  ? `${formatPrice(pointBalance - getFinalPrice())}P`
                                  : `${formatPrice(getFinalPrice() - pointBalance)}P 부족`}
                              </span>
                            </div>
                            {pointBalance >= getFinalPrice() ? (
                              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1 font-semibold">
                                <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center"></i>
                                포인트로 결제 가능합니다!
                              </p>
                            ) : (
                              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1 font-semibold">
                                <i className="ri-error-warning-fill w-3 h-3 flex items-center justify-center"></i>
                                포인트가 부족합니다. 전산에서 포인트를 충전해주세요.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="bg-white/70 rounded-lg p-3">
                        <p className="text-xs text-emerald-700 font-semibold mb-1 flex items-center gap-1">
                          <i className="ri-information-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          포인트 적립 방법
                        </p>
                        <p className="text-xs text-emerald-600 leading-relaxed">
                          ERP 전산 시스템에서 [쇼핑몰로 포인트 넘기기] 버튼을 클릭하시면 포인트가 자동으로 적립됩니다.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 오류 메시지 */}
                  {payError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-red-600 flex-shrink-0 mt-0.5"></i>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-800 mb-1">결제 오류</p>
                          <p className="text-sm text-red-700">{payError}</p>
                          {!isSubmitting && (
                            <button type="button" onClick={handleRetryPayment}
                              className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                              다시 시도하기
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 버튼 영역 */}
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep('info')} disabled={isSubmitting}
                      className="flex-1 py-3.5 border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-emerald-500 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                      이전으로
                    </button>
                    <button type="submit" disabled={isSubmitting || (paymentMethod === 'shop_point' && (!pointChecked || (pointBalance !== null && pointBalance < getFinalPrice())))}
                      className={`flex-[2] py-3.5 text-white rounded-full font-bold transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 ${
                        paymentMethod === 'kakaopay' ? 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                        : paymentMethod === 'naverpay' ? 'bg-green-500 hover:bg-green-600'
                        : paymentMethod === 'point' ? 'bg-teal-500 hover:bg-teal-600'
                        : paymentMethod === 'shop_point' ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}>
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                          처리 중...
                        </>
                      ) : paymentMethod === 'card' ? (
                        <>
                          <i className="ri-bank-card-line w-4 h-4 flex items-center justify-center"></i>
                          카드결제 (홀빅 PG)
                        </>
                      ) : paymentMethod === 'kakaopay' ? (
                        <><span className="font-black">K</span>카카오페이로 결제하기</>
                      ) : paymentMethod === 'naverpay' ? (
                        <><span className="font-black">N</span>네이버페이로 결제하기</>
                      ) : paymentMethod === 'point' ? (
                        <>
                          <span className="text-xs font-black">USDT</span>
                          포인트 결제로 주문 완료
                        </>
                      ) : paymentMethod === 'shop_point' ? (
                        <>
                          <i className="ri-coin-line w-4 h-4 flex items-center justify-center"></i>
                          쇼핑 포인트로 결제하기
                        </>
                      ) : (
                        '계좌이체로 주문 완료'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* 오른쪽: 주문 요약 */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 space-y-4">

                {/* 주문 상품 카드 */}
                <div className="bg-white rounded-2xl overflow-hidden">
                  <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                      주문 상품
                    </h3>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-semibold">
                      {items.reduce((acc, i) => acc + i.quantity, 0)}개
                    </span>
                  </div>
                  <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-3 items-start bg-gray-50 rounded-xl p-3">
                        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-gray-100">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 leading-snug mb-0.5">{item.name}</p>
                          <p className="text-xs text-gray-400 mb-1.5">{item.category}</p>
                          <div className="flex items-center justify-between">
                            <span className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-0.5 text-xs text-gray-600 font-semibold">
                              <i className="ri-box-3-line w-3 h-3 flex items-center justify-center text-emerald-500"></i>
                              수량 {item.quantity}개
                            </span>
                            <p className="text-sm font-bold text-emerald-600 whitespace-nowrap">{formatPrice(item.price * item.quantity)}원</p>
                          </div>
                          {item.originalPrice && item.originalPrice > item.price && (
                            <p className="text-xs text-gray-400 line-through text-right mt-0.5">{formatPrice(item.originalPrice * item.quantity)}원</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 쿠폰/할인코드 */}
                <div className="bg-white rounded-2xl p-5">
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <i className="ri-coupon-3-line w-4 h-4 flex items-center justify-center text-emerald-600"></i>
                    쿠폰 / 할인코드
                  </p>
                  {appliedCoupon ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                            <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center"></i>
                            {appliedCoupon.label}
                          </p>
                          <p className="text-xs text-emerald-600 mt-1">코드: <span className="font-mono font-bold">{appliedCoupon.code}</span></p>
                          <p className="text-xs font-bold text-red-500 mt-1">-{formatPrice(getDiscountAmount())}원 할인 적용</p>
                        </div>
                        <button type="button" onClick={handleRemoveCoupon}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer flex-shrink-0 mt-0.5">
                          <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input type="text" value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                        placeholder="쿠폰 코드를 입력"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono uppercase" />
                      <button type="button" onClick={handleApplyCoupon} disabled={isCouponApplying}
                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center gap-1">
                        {isCouponApplying ? <i className="ri-loader-4-line animate-spin w-3 h-3 flex items-center justify-center"></i> : '적용'}
                      </button>
                    </div>
                  )}
                  {couponError && (
                    <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                      <i className="ri-error-warning-line w-3 h-3 flex items-center justify-center"></i>{couponError}
                    </p>
                  )}
                  {couponSuccess && (
                    <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line w-3 h-3 flex items-center justify-center"></i>{couponSuccess}
                    </p>
                  )}
                </div>

                {/* 배송비 안내 */}
                <div className="bg-white rounded-2xl p-5">
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <i className="ri-truck-line w-4 h-4 flex items-center justify-center text-emerald-600"></i>
                    배송 안내
                  </p>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between bg-emerald-50 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-truck-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">배송비</span>
                      </div>
                      <span className="text-sm font-black text-emerald-600 flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill w-3.5 h-3.5 flex items-center justify-center"></i>
                        전국 무료
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">출고 소요</p>
                        <p className="text-sm font-bold text-gray-800">1~2일</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">배송 소요</p>
                        <p className="text-sm font-bold text-gray-800">2~3일</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 px-1">
                      <i className="ri-information-line text-gray-400 w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <p className="text-xs text-gray-400 leading-relaxed">제주·도서산간 지역은 추가 1~2일 소요될 수 있습니다.</p>
                    </div>
                  </div>
                </div>

                {/* 금액 요약 */}
                <div className="bg-white rounded-2xl p-5">
                  <p className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-1.5">
                    <i className="ri-receipt-line w-4 h-4 flex items-center justify-center text-emerald-600"></i>
                    결제 금액 요약
                  </p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">상품 금액</span>
                      <span className="font-semibold text-gray-800">{formatPrice(getTotalPrice())}원</span>
                    </div>
                    {getDiscountAmount() > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-red-500 font-semibold flex items-center gap-1">
                          <i className="ri-coupon-3-line w-3 h-3 flex items-center justify-center"></i>
                          쿠폰 할인
                        </span>
                        <span className="text-red-500 font-bold">-{formatPrice(getDiscountAmount())}원</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500 flex items-center gap-1">
                        <i className="ri-truck-line w-3 h-3 flex items-center justify-center"></i>
                        배송비
                      </span>
                      <span className="text-emerald-600 font-bold">무료</span>
                    </div>
                    <div className="border-t border-dashed border-gray-200 pt-3 mt-1">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900 text-sm">총 결제금액</span>
                        <div className="text-right">
                          {getDiscountAmount() > 0 && (
                            <p className="text-xs text-gray-400 line-through mb-0.5">{formatPrice(getTotalPrice())}원</p>
                          )}
                          <span className="text-xl font-black text-emerald-600">{formatPrice(getFinalPrice())}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <i className="ri-shield-check-line text-emerald-500 w-4 h-4 flex items-center justify-center"></i>
                      <span>홀빅(Payster) SSL 안전결제 보호</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <i className="ri-refresh-line text-emerald-500 w-4 h-4 flex items-center justify-center"></i>
                      <span>30일 이내 교환·반품 가능</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* 상품평 섹션 */}
          {(() => {
            const cartProductIds = items.map((i) => String(i.id));
            const relevantReviews = mockReviews
              .filter((r) => cartProductIds.includes(String(r.productId)))
              .slice(0, 6);
            const showReviews = relevantReviews.length > 0 ? relevantReviews : mockReviews.slice(0, 6);
            const avgRating = (showReviews.reduce((acc, r) => acc + r.rating, 0) / showReviews.length).toFixed(1);

            return (
              <div className="mt-8 bg-white rounded-2xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                      구매 고객 상품평
                    </h3>
                    <p className="text-sm text-gray-500">실제 구매 고객들의 생생한 후기입니다</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-1 mb-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className={`ri-star-${Number(avgRating) >= s ? 'fill' : 'line'} text-sm w-4 h-4 flex items-center justify-center ${Number(avgRating) >= s ? 'text-amber-400' : 'text-gray-200'}`}></i>
                      ))}
                    </div>
                    <p className="text-xl font-black text-gray-900">{avgRating} <span className="text-sm font-normal text-gray-400">/ 5.0</span></p>
                    <p className="text-xs text-gray-400">{showReviews.length}개 리뷰</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {showReviews.map((review) => {
                    const relatedItem = items.find((i) => String(i.id) === String(review.productId));
                    return (
                      <div key={review.id} className="bg-gray-50 rounded-xl p-4 flex flex-col gap-3">
                        {/* 별점 + 상품명 */}
                        <div>
                          <div className="flex items-center gap-0.5 mb-1.5">
                            {[1,2,3,4,5].map((s) => (
                              <i key={s} className={`ri-star-${review.rating >= s ? 'fill' : 'line'} text-xs w-3 h-3 flex items-center justify-center ${review.rating >= s ? 'text-amber-400' : 'text-gray-200'}`}></i>
                            ))}
                            {review.verified && (
                              <span className="ml-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <i className="ri-checkbox-circle-fill w-2.5 h-2.5 flex items-center justify-center"></i>
                                구매확인
                              </span>
                            )}
                          </div>
                          {relatedItem && (
                            <p className="text-[10px] text-emerald-600 font-semibold mb-1 truncate">
                              <i className="ri-shopping-bag-line w-3 h-3 flex items-center justify-center inline-flex mr-0.5"></i>
                              {relatedItem.name}
                            </p>
                          )}
                          <p className="text-sm font-bold text-gray-900 leading-snug">{review.title}</p>
                        </div>
                        {/* 리뷰 내용 */}
                        <p className="text-xs text-gray-600 leading-relaxed flex-1 line-clamp-3">{review.body}</p>
                        {/* 작성자 정보 */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full text-xs font-black text-emerald-700">
                              {review.avatar}
                            </div>
                            <span className="text-xs font-semibold text-gray-700">{review.author}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-400">{review.date.slice(0, 7)}</span>
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <i className="ri-thumb-up-line w-3 h-3 flex items-center justify-center"></i>
                              {review.helpful}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 text-center">
                  <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-semibold hover:text-emerald-700 transition-colors cursor-pointer">
                    전체 상품평 보러가기
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            );
          })()}

        </div>
      </div>
      <PageFooter />
    </>
  );
}
